import { AuthEvent } from '@/features/Auth/machine/states'
import { hasStatus } from '@/services/api/errorStatus'
import { retrieve } from '@/services/api/session/views'

// `profiles/i/` is IsAuthenticated, and the backend resolves a token whose uid matches no
// Profile to an unauthenticated request, so "this server has never seen you" arrives as a
// 401/403/404. Every other outcome means the lookup itself failed and says nothing about
// whether the person is registered — reading those as "new user" is what routed registered
// people into signup on a 5xx or a dropped connection. See EPIC-004.
const PROFILE_UNKNOWN_STATUSES = [401, 403, 404]

export type Emit = (event: AuthEvent) => void

export async function resolveProfile(emit: Emit): Promise<void> {
  try {
    await retrieve()

    // Existence only. Whether the profile is *complete* is a different question, asked by
    // `ensureProfileCompletion` when an action needs the answer — reviewing, publishing a
    // story, finishing a lesson. Asking it here turned every sign-in into a demand for
    // details the product only wants at those moments.
    emit({ type: 'profileResolved' })
  } catch (error) {
    // A request that never reached the server has no status at all, and that is exactly the
    // case that must not read as "no profile here". `getErrorStatus` is what knows where the
    // status actually lives — the client rejects with an `APIError`, not the axios error, so
    // reading `error.response.status` here returned `undefined` for every outcome alike and
    // sent registered people down the `failed` branch on a plain 403.
    if (hasStatus(error, PROFILE_UNKNOWN_STATUSES)) {
      emit({ type: 'profileAbsent' })
      return
    }

    emit({ type: 'failed', reason: 'unavailable' })
  }
}
